import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const shopRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (shopRef.current && !shopRef.current.contains(event.target as Node)) {
        setShopDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = (slug: string) => {
    setMobileOpen(false);
    navigate(slug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
        <nav className="max-w-[1280px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group cursor-pointer flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-white text-lg"></i>
            </div>
            <span className="font-heading font-bold text-lg text-foreground-900 whitespace-nowrap">
              VigorBuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>

            <div className="relative" ref={shopRef}>
              <button
                onClick={() => setShopDropdownOpen(!shopDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shopping Guide
                <i className={`${shopDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs transition-transform`}></i>
              </button>

              {shopDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-white rounded-2xl shadow-2xl border border-background-200 overflow-hidden z-50 max-h-[480px] overflow-y-auto">
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wide">
                      Product Categories
                    </div>
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setShopDropdownOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors flex-shrink-0">
                          <i className={`${cat.icon} text-primary-600 text-sm`}></i>
                        </div>
                        <span className="font-medium whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/article/vigorbuy-spreadsheet-comparison-guide" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Comparison
            </Link>
            <Link to="/article/vigorbuy-spreadsheet-safety-guide" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Safety
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer"
            >
              VigorBuy Spreadsheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 hover:bg-background-200 active:bg-background-300 transition-colors cursor-pointer"
            aria-label="Menu"
          >
            {mobileOpen ? (
              <i className="ri-close-line text-foreground-700 text-xl"></i>
            ) : (
              <i className="ri-menu-line text-foreground-700 text-xl"></i>
            )}
          </button>
        </nav>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] md:hidden"
          onClick={() => setMobileOpen(false)}
          style={{ touchAction: 'none' }}
        />
      )}

      {/* Mobile slide-out panel */}
      <div
        className={`fixed top-0 right-0 h-[100dvh] w-[320px] max-w-[85vw] bg-white z-[70] shadow-[0_0_40px_rgba(0,0,0,0.25)] flex flex-col md:hidden transition-transform duration-300 ease-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile panel header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-background-200 flex-shrink-0 bg-white">
          <span className="font-heading font-bold text-base text-foreground-900">Menu</span>
          <button
            onClick={() => setMobileOpen(false)}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 hover:bg-background-200 active:bg-background-300 transition-colors cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-foreground-700 text-xl"></i>
          </button>
        </div>

        {/* Mobile panel scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain bg-white">
          <div className="px-4 py-5 space-y-6">

            {/* Quick actions */}
            <div className="space-y-2">
              <button
                onClick={() => handleNavClick('/')}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-4-line text-white text-base"></i>
                </div>
                <span className="font-semibold text-sm">Home</span>
              </button>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shopping-bag-3-line text-primary-600 text-base"></i>
                </div>
                <span className="font-semibold text-sm">VigorBuy Spreadsheet</span>
                <i className="ri-arrow-right-up-line text-foreground-400 text-sm ml-auto flex-shrink-0"></i>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-flashlight-line text-white text-base"></i>
                </div>
                <span className="font-semibold text-sm">Buy It Now</span>
                <i className="ri-arrow-right-up-line text-sm ml-auto flex-shrink-0"></i>
              </a>
            </div>

            {/* Categories section */}
            <div>
              <div className="px-3 py-2 mb-1 text-xs font-bold text-foreground-400 uppercase tracking-wider">
                Shopping Guide — Categories
              </div>
              <div className="space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-primary-600 text-sm`}></i>
                    </div>
                    <span className="font-medium whitespace-nowrap">{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs ml-auto flex-shrink-0"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* SEO Guides section */}
            <div className="border-t border-background-200 pt-5">
              <div className="px-3 py-2 mb-1 text-xs font-bold text-foreground-400 uppercase tracking-wider">
                SEO Guides
              </div>
              <div className="space-y-0.5">
                <button
                  onClick={() => handleNavClick('/article/vigorbuy-spreadsheet-comparison-guide')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer text-left"
                >
                  <i className="ri-scales-3-line text-foreground-500 text-base w-5 text-center flex-shrink-0"></i>
                  <span className="font-medium">Comparison Guide</span>
                </button>
                <button
                  onClick={() => handleNavClick('/article/vigorbuy-spreadsheet-safety-guide')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer text-left"
                >
                  <i className="ri-shield-check-line text-foreground-500 text-base w-5 text-center flex-shrink-0"></i>
                  <span className="font-medium">Safety Guide</span>
                </button>
                <button
                  onClick={() => handleNavClick('/article/vigorbuy-spreadsheet-beginner-tips-guide')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer text-left"
                >
                  <i className="ri-lightbulb-line text-foreground-500 text-base w-5 text-center flex-shrink-0"></i>
                  <span className="font-medium">Beginner Tips</span>
                </button>
                <button
                  onClick={() => handleNavClick('/article/vigorbuy-spreadsheet-2026-update-guide')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer text-left"
                >
                  <i className="ri-calendar-check-line text-foreground-500 text-base w-5 text-center flex-shrink-0"></i>
                  <span className="font-medium">2026 Update Guide</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}